import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { NewUserProjectComponent } from '../../../../layouts/my-projects/new-project/project-permissions/new-user-project/new-user-project.component';
import { ProjectSortingCriteria } from '../../../../shared/pipes/filter-sorting-project.pipe';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';
import { AuthService } from '../../../../shared/services/auth.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { PermissionService } from '../../../../shared/services/permissions.service';
import { ProjectService } from '../../../../shared/services/project.service';
import { activeLanguageUpdated } from '../../../../shared/services/translate.service';
import { UserProjectPermissionInfo } from '../../../models/project.model';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { WorkspaceProjectPermissionTableHeader } from './table/header/workspace-project-permission-table-header.component';
import { workspaceProjectParticipantColumnsDef } from './table/workspace-project-participant-table.coldef';
import { CompanyService } from '../../../../shared/services/company.service';
import { GoogleAnalyticsService } from '../../../../shared/services/google-analytics.service';
import {
  ACTIVATION_STATUSES as CONST_ACTIVATION_STATUSES,
  ROUTES,
  GRID_DEFAULTS,
  LABEL_KEYS,
  TABLE_COMPONENTS,
  USER_INVITATION_MESSAGES,
  COMPANY_TYPE,
} from './constants/workspace-projects.constants';
import { CompanyInfo, Project, User, WorkspaceDTO } from '../../../../shared/interfaces';
import { ProjectUserService } from '../../../../shared/services/project-user.service';

@Component({
  selector: 'mtm-workspace-projects',
  templateUrl: './workspace-projects.component.html',
  styleUrl: './workspace-projects.component.scss',
  standalone: false,
})
export class WorkspaceProjectsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(AgGridAngular) workspaceProjectParticipantsTable!: AgGridAngular;

  @Input() companyUsers: User[];
  @Input() setBreadcrumb: () => void;

  private readonly ngUnsubscribe = new Subject();

  private _workspace: WorkspaceDTO = null;

  private readonly projectsSignal = signal<Project[]>([]);
  private readonly selectedProjectSignal = signal<Project | null>(null);
  private readonly projectUsersSignal = signal<UserProjectPermissionInfo[]>([]);
  private readonly participantsSignal = signal<UserProjectPermissionInfo[]>([]);
  private readonly projectSortingSignal = signal<any>({ btn: ProjectSortingCriteria.ProjectName, sortDirection: true });
  private readonly localFilterParamsSignal = signal<{
    fullName: string;
    email: string;
    decisionMaker: any;
  }>({ fullName: '', email: '', decisionMaker: '' });
  private readonly canEditProjectSignal = signal<boolean>(false);

  get projects(): Project[] {
    return this.projectsSignal();
  }
  set projects(value: Project[]) {
    this.projectsSignal.set(value ?? []);
  }

  get selectedProject(): Project | null {
    return this.selectedProjectSignal();
  }
  set selectedProject(value: Project | null) {
    this.selectedProjectSignal.set(value ?? null);
  }

  get projectUsers(): UserProjectPermissionInfo[] {
    return this.projectUsersSignal();
  }
  set projectUsers(value: UserProjectPermissionInfo[]) {
    this.projectUsersSignal.set(value ?? []);
  }

  get participants(): UserProjectPermissionInfo[] {
    return this.participantsSignal();
  }
  set participants(value: UserProjectPermissionInfo[]) {
    this.participantsSignal.set(value ?? []);
  }

  isAdmin: boolean = false;
  isProductionSide: boolean = false;
  canCreateProject: boolean = false;
  get canEditProject(): boolean {
    return this.canEditProjectSignal();
  }
  set canEditProject(value: boolean) {
    this.canEditProjectSignal.set(!!value);
  }

  ProjectSortCriteria = ProjectSortingCriteria;
  get projectSorting(): any {
    return this.projectSortingSignal();
  }
  set projectSorting(value: any) {
    this.projectSortingSignal.set(value ?? { btn: ProjectSortingCriteria.ProjectName, sortDirection: true });
  }

  company: CompanyInfo = null;
  hasMarketplaceAccess: boolean = false;

  gridOptions = GRID_DEFAULTS;

  activationStatuses = CONST_ACTIVATION_STATUSES;

  tableContext = { componentParent: this };
  components: { [p: string]: any } = { [TABLE_COMPONENTS.HEADER]: WorkspaceProjectPermissionTableHeader };

  get localFilterParams(): { fullName: string; email: string; decisionMaker: any } {
    return this.localFilterParamsSignal();
  }
  set localFilterParams(value: { fullName: string; email: string; decisionMaker: any }) {
    this.localFilterParamsSignal.set(value ?? { fullName: '', email: '', decisionMaker: '' });
  }

  authUserHaveAValidationRight: boolean;

  defaultColDef: ColDef = {
    sortable: false,
    resizable: true,
    filter: false,
    headerValueGetter: this.getHeaderText.bind(this),
  };

  workspaceProjectParticipantColumnsDef: ColDef[] = workspaceProjectParticipantColumnsDef;

  constructor(
    private readonly authService: AuthService,
    private readonly companyService: CompanyService,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly projectService: ProjectService,
    private readonly projectUserService: ProjectUserService,
    private readonly permissionService: PermissionService,
    private readonly translatePipe: TranslatePipe,
    private readonly modalService: NgbModal
  ) {
    this.tableContext = { componentParent: this };
  }

  get workspace(): WorkspaceDTO {
    return this._workspace;
  }

  @Input()
  set workspace(value: WorkspaceDTO) {
    this._workspace = value;
    this.resetWorkspaceState();
    this.loadProjects();
  }

  ngOnInit(): void {
    this.googleAnalyticsService.sendUserAnalyticsConfig('', '', '');
    this.initializePermissionsAndFlags();
    this.subscribeLanguageChanges();
    this.fetchCompany();
    if (this.setBreadcrumb) {
      this.setBreadcrumb();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.refreshTableHeader();
    }, 100);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  private resetWorkspaceState() {
    this.projects = [];
    this.participants = [];
    this.selectedProject = null;
    this.projectUsers = [];
  }

  private initializePermissionsAndFlags() {
    if (this.permissionService.isPlatformAdmin()) {
      this.isAdmin = true;
    }
    const authUser = this.authService.getAuthUser();
    this.isProductionSide = authUser.company.companyType === COMPANY_TYPE.PRODUCER;
    this.canCreateProject = this.permissionService.canCreateProject();
  }

  private subscribeLanguageChanges() {
    activeLanguageUpdated.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      setTimeout(() => this.refreshTableHeader(), 100);
    });
  }

  private fetchCompany() {
    const companyId = this.authService.getAuthUser().company.id;
    this.companyService
      .getCompanyById(companyId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (company) => {
          this.company = company;
          this.hasMarketplaceAccess = company.companyType === COMPANY_TYPE.PRODUCER && !!company.marketplaceAccess;
        },
      });
  }

  private refreshTableHeader() {
    this.workspaceProjectParticipantsTable?.api?.refreshHeader();
  }

  loadProjects() {
    if (!this._workspace) {
      return;
    }
    this.projectService
      .getProjects()
      .pipe(take(1))
      .subscribe((projects: Project[]) => {
        if (!projects) return;
        const filtered = this.filterWorkspaceProjects(projects, this._workspace.id);
        this.projects = this.sortProjectsByName(filtered);
        if (this.projects?.length) {
          this.selectProjectItem(this.projects[0]);
        }
      });
  }

  private filterWorkspaceProjects(projects: Project[], workspaceId: string) {
    return projects.filter((p: Project) => p.workspaceId == workspaceId).map((p: Project) => ({ ...p, localId: p.id }));
  }

  private sortProjectsByName(projects: Project[]) {
    return projects.sort((a: Project, b: Project) =>
      a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase() ? -1 : 1
    );
  }

  createProject() {
    if (!this._workspace) return;
    if (!this.canCreateProject) return;
    if (this.projectService.projectOnloaded) this.projectService.leaveFromProject();
    this.navigateToCreateProject();
  }

  private navigateToCreateProject() {
    const params = { workspaceId: this._workspace.id };
    void this.router.navigate([ROUTES.PROJECT_NEW], { queryParams: params });
  }

  selectProjectItem(project: Project) {
    this.setSelectedProject(project);
    this.initDecision();
    this.loadParticipants(project);
    this.canEditProject = this.permissionService.canUpdateProject();
  }

  private setSelectedProject(project: Project) {
    this.authUserHaveAValidationRight = this.permissionService.isProjectValidator(project);
    this.selectedProject = project;
    this.setValidationMode(this.selectedProject);
  }

  private setValidationMode(project: Project) {
    project.validationMode = (project.validators?.length || 0) <= 1 ? 'ONE' : 'ALL';
  }

  public loadParticipants(project: Project) {
    this.participants = [];
    this.projectUsers = [];
    this.projectUserService
      .getProjectParticipantWithUserActivationStatus(project.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((participants: any) => {
        this.projectUsers = participants || [];
        this.participants = (participants || []).map((element: any, index: number) =>
          this.mapParticipant(element, index)
        );
      });
  }

  private mapParticipant(element: any, index: number) {
    const { username = '', userActivationStatus = '' } = element || {};
    const statusInfo = this.activationStatuses.find((s: any) => s.code == userActivationStatus);
    const statusLabel = statusInfo ? statusInfo.label : '';
    const user: UserProjectPermissionInfo = {
      username,
      avatarUrl: '',
      fullName: element.fullName ? element.fullName.trim() : '',
      jobTitle: '',
      status: userActivationStatus,
      statusLabel,
    };
    if (this.companyUsers) {
      const existingUser = this.companyUsers.find((u) => u.username == username);
      if (existingUser) {
        user.avatarUrl = existingUser.avatarUrl;
        user.jobTitle = existingUser.jobTitle;
        user.userData = existingUser;
      }
    }
    user.index = index;
    this.configureOdmForUser(user);
    return user;
  }

  configureOdmForUser(user: UserProjectPermissionInfo) {
    // Decision-maker state comes solely from the project's validators attribute now that
    // participant roles are no longer serialized.
    user.selectOdm = !!this.selectedProject?.validators?.includes(user.username);
    user.showOdm = user.selectOdm;
    if (this.participants.length > 0) {
      const index = this.participants.findIndex((u) => u.username == user.username);
      if (index > -1) {
        this.participants[index].showOdm = user.showOdm;
        this.participants[index].selectOdm = user.selectOdm;
      }
    }
  }

  showAddModal() {
    if (!this.selectedProject || !this.canEditProject) return;
    const modal = this.modalService.open(NewUserProjectComponent, { size: 'lg', backdrop: 'static', keyboard: false });
    modal.componentInstance.users = this.projectUsers;
    modal.componentInstance.project = this.selectedProject;
    modal.componentInstance.projectId = this.selectedProject.id;
    modal.result.then(
      (result: any) => {
        console.log('Modal closed with result:', result);
        if (result) {
          if (result.user) {
            const filtered = this.companyUsers.filter((u) => u.username == result.user.username);
            if (filtered.length > 0) {
              this.notificationService.open(USER_INVITATION_MESSAGES.ALREADY_EXISTS);
            }
          } else if (result.invited) {
            this.notificationService.open(USER_INVITATION_MESSAGES.INVITE_SENT(result.email));
          }
          this.loadParticipants(this.selectedProject);
        }
      },
      () => {}
    );
    return modal;
  }

  goToProject() {
    if (!this.selectedProject) return;
    void this.router.navigate([ROUTES.PROJECT_DASHBOARD, this.selectedProject.id]);
  }

  decision: boolean;

  private initDecision() {
    if (this.selectedProject?.validationMode == 'ALL') {
      this.decision = true;
    } else if (this.selectedProject?.validationMode == 'ONE') {
      this.decision = false;
    }
  }

  changeOdmDecision(user: UserProjectPermissionInfo, e: Event) {
    const project = this.selectedProject;
    if (!this.authUserHaveAValidationRight || !project) {
      return;
    }

    const target = e.target as HTMLInputElement;
    const { validators } = project;
    if (validators.includes(user.username)) {
      // The last remaining validator cannot be removed.
      if (validators.length <= 1) {
        user.selectOdm = true;
        target.checked = true;
        this.projectService.saveProject(project);
        return;
      }
      const index = validators.findIndex((k) => k == user.username);
      if (index >= 0) {
        user.selectOdm = false;
        target.checked = false;
        validators.splice(index, 1);
      }
    } else {
      user.selectOdm = true;
      target.checked = true;
      validators.push(user.username);
    }
    this.setValidationMode(project);
    this.initDecision();
    this.projectService.saveProject(project);
  }

  updateProjectSorting(criteria: string, ascending: boolean) {
    this.projectSorting = { btn: criteria, sortDirection: ascending };
  }

  getHeaderText(params: any) {
    let headerText = '';
    switch (params.colDef.field) {
      case 'fullName':
        headerText = LABEL_KEYS.USER;
        break;
      case 'email':
        headerText = LABEL_KEYS.EMAIL;
        break;
      case 'rights':
        headerText = LABEL_KEYS.RIGHTS;
        break;
      case 'decisionMaker':
        headerText = LABEL_KEYS.DECISION_MAKER;
        break;
      case 'action':
        headerText = LABEL_KEYS.ACTION;
        break;
    }
    return this.translatePipe.transform(headerText);
  }
}
